import angular from "angular";
import { IFormOptionsAPI, ITemplateScope } from "AngularFormly";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../records/forms/formly/fieldConfigurationHelpers";
import { RecordDataSource } from "../../../records/grids";
import {
  CellDoubleClickedEventHandlerArgs,
  DoubleClickHandlerService,
  vsName as DoubleClickHandlerServiceName,
} from "../../../records/grids/cells/doubleClickHandlerService";
import { FilterProvider } from "../../../records/grids/columns/filters/FilterProvider";
import { LookupFilter } from "../../../records/grids/columns/filters/subFilters/LookupFilter";
import { OptionsFilter } from "../../../records/grids/columns/filters/subFilters/OptionsFilter";
import {
  vsName as gridSettingsServiceName,
  GridSettingsService,
  IGridSettings,
} from "../../../records/grids/settings/gridSettingsService";
import {
  booleanValueGetterFactory,
  lookupValueGetterFactory,
  moneyValueGetterFactory,
  percentageValueGetterFactory,
} from "../../../records/grids/cells/valueGetters";
import { PatchObject, Record } from "../../../records/server/crud";
import {
  vsName as ServerUtilityName,
  ServerUtility,
} from "../../../records/server/serverUtility";
import fieldHelpers from "../../../ui-formly/fieldHelpers";

import { AngularGridTemplates } from "../../../utils/AngularGrid/templates";
import { ColDef } from "../../../records/grids/columns/colDef";
import { ProgressivePermissions } from "../../permissions";

const progressiveDefinitionGridSettingsId = Symbol(
  "progressiveDefinitionGridSettings"
);

const progressiveDefinitionUrl =
  "/api/machines/progressives/ProgressiveDefinition";
const progressiveMeterReadingSettingUrl =
  "/api/machines/progressives/ProgressiveMeterReadingSetting";
const progressiveControllerTypeUrl =
  "/api/machines/progressives/ProgressiveControllerType";

export class ProgressiveDefinitionViewController {
  static $inject = [
    "$compile",
    gridSettingsServiceName,
    ServerUtilityName,
    DoubleClickHandlerServiceName,
    FieldConfigurationHelperServiceName,
    "angularGridTemplates",
  ];

  gridDataSource: RecordDataSource;
  gridSettings: IGridSettings;
  columns: ColDef[];

  constructor(
    $compile: angular.ICompileService,
    gridSettingsService: GridSettingsService,
    private readonly serverUtility: ServerUtility,
    doubleClickHandlerService: DoubleClickHandlerService,
    fieldConfigurationHelperService: FieldConfigurationHelperService,
    angularGridTemplates: AngularGridTemplates
  ) {
    this.gridDataSource = new RecordDataSource(
      serverUtility,
      progressiveDefinitionUrl
    );
    this.gridSettings = gridSettingsService.getGridSettings(
      progressiveDefinitionGridSettingsId
    );
    this.columns = [
      {
        colId: "detail",
        headerName: "Info",
        width: 20,
        template: angularGridTemplates.detailUrl(
          ".detail.machines({ id: data.Id})"
        ),
        editable: false,
        suppressMenu: true,
        suppressSorting: true,
      },
      {
        colId: "edit",
        headerName: "Edit",
        width: 20,
        template: angularGridTemplates.editUrl(".detail.edit({ id: data.Id})"),
        editable: false,
        suppressMenu: true,
        suppressSorting: true,
        requiresPermission: ProgressivePermissions.EditPermission,
      },
      {
        field: "Id",
        headerName: "Id",
        filter: new FilterProvider("Id", $compile)
          .defaultNumberFilters()
          .getGridFilterFactory(),
        hide: true,
      },
      {
        field: "Name",
        headerName: "Name",
        filter: new FilterProvider("Name", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["Id"],
              serverQuery: (search: string) =>
                serverUtility.lookupQuery({
                  url: progressiveDefinitionUrl,
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          submit: (patchObject) => this.patchRecord(patchObject),
          fetchRecord: (args) => this.fetchRecord(args),
          patchField: {
            formField: {
              type: "gridInput",
              name: "Name",
              key: "Name",
              templateOptions: {
                label: "Name",
                required: true,
              },
              modelOptions: {
                debounce: 500,
              },
              asyncValidators: {
                unique: (
                  $viewValue: any,
                  $modelValue: any,
                  scope: ITemplateScope
                ) => {
                  const id = scope.model?.Id;
                  return serverUtility
                    .alreadyExists({
                      url: progressiveDefinitionUrl,
                      query: { Name: $viewValue },
                      excludeId: id,
                    })
                    .then((alreadyTaken) => {
                      if (alreadyTaken) {
                        throw new Error();
                      }
                    });
                },
              },
            },
          },
        }),
      },
      {
        field: "ManufacturerName",
        headerName: "Manufacturer",
        filter: new FilterProvider("ManufacturerName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["ManufacturerId"],
              serverQuery: (search: string) => {
                return serverUtility.lookupQueryLegacy({
                  url: "/api/machines/manufacturers",
                  search,
                });
              },
            })
          )
          .getGridFilterFactory(),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          patchField: {
            formField: fieldConfigurationHelperService.gridSelectAsyncField({
              key: "ManufacturerId",
              name: "ManufacturerId",
              label: "Manufacturer",
              queryUrl: "/api/machines/manufacturers",
              getDefaultItem: (model: any) => {
                return {
                  Id: model.ManufacturerId,
                  Name: model.ManufacturerName,
                };
              },
              useLegacyQuery: true,
            }),
            referenceFieldName: "ManufacturerName",
          },
          submit: (patchObject: PatchObject) => this.patchRecord(patchObject),
          fetchRecord: (args) => this.fetchRecord(args),
        }),
      },
      {
        field: "ProgressiveMeterReadingSettingName",
        headerName: "Meter Reading Setting",
        filter: new FilterProvider(
          "ProgressiveMeterReadingSettingName",
          $compile
        )
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["ProgressiveMeterReadingSettingId"],
              serverQuery: (search: string) =>
                serverUtility.lookupQuery({
                  url: progressiveMeterReadingSettingUrl,
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          patchField: {
            formField: fieldConfigurationHelperService.gridSelectAsyncField({
              getDefaultItem: (model) => {
                return {
                  Id: model.ProgressiveMeterReadingSettingId,
                  Name: model.ProgressiveMeterReadingSettingName,
                };
              },
              queryUrl: progressiveMeterReadingSettingUrl,
              required: true,
              label: "Meter Reading Setting",
              key: "ProgressiveMeterReadingSettingId",
              name: "ProgressiveMeterReadingSettingId",
            }),
            referenceFieldName: "ProgressiveMeterReadingSettingName",
          },
          submit: (patchObject) => this.patchRecord(patchObject),
          fetchRecord: (args) => this.fetchRecord(args),
        }),
      },
      {
        field: "ProgressiveMeterReadingSettingMeterReadingRequired",
        headerName: "Meter Reading Required",
        valueGetter: booleanValueGetterFactory(),
        filter: new FilterProvider(
          "ProgressiveMeterReadingSettingMeterReadingRequired",
          $compile
        )
          .defaultBooleanFilters()
          .getGridFilterFactory(),
      },
      {
        field: "ProgressiveMeterReadingSettingComparisonField",
        headerName: "Comparison Field",
        valueGetter: lookupValueGetterFactory({
          lookup: { ResetBase: "Reset Base", LevelReading: "Level Reading" },
        }),
        filter: new FilterProvider(
          "ProgressiveMeterReadingSettingComparisonField",
          $compile
        )
          .addFilter(
            "=",
            new OptionsFilter({
              displayName: "=",
              options: [
                {
                  display: "Reset Base",
                  value: "ResetBase",
                },
                {
                  display: "Level Reading",
                  value: "LevelReading",
                },
              ],
              queryFieldNames: [
                "ProgressiveMeterReadingSettingComparisonField",
              ],
              includeNullValue: true,
            })
          )
          .getGridFilterFactory(),
      },
      {
        field: "ProgressiveMeterReadingSettingComparisonValue",
        headerName: "Comparison Value",
        filter: new FilterProvider(
          "ProgressiveMeterReadingSettingComparisonValue",
          $compile
        )
          .defaultNumberFilters()
          .getGridFilterFactory(),
        valueGetter: moneyValueGetterFactory(),
      },
      {
        field: "ProgressiveMeterReadingSettingEveryXDays",
        headerName: "Read every X days",
        filter: new FilterProvider(
          "ProgressiveMeterReadingSettingEveryXDays",
          $compile
        )
          .defaultNumberFilters()
          .getGridFilterFactory(),
      },
      {
        field: "ProgressiveMeterReadingSettingAllReadingsRequiredDayOfMonth",
        headerName: "All Readings Required on Day",
        filter: new FilterProvider(
          "ProgressiveMeterReadingSettingAllReadingsRequiredDayOfMonth",
          $compile
        )
          .defaultNumberFilters()
          .getGridFilterFactory(),
      },
      {
        field: "Status",
        headerName: "Status",
        filter: new FilterProvider("Status", $compile)
          .addFilter(
            "=",
            new OptionsFilter({
              displayName: "=",
              options: [
                { display: "Active", value: "Active" },
                { display: "Inactive", value: "Inactive" },
              ],
              queryFieldNames: ["Status"],
              includeNullValue: true,
            })
          )
          .getGridFilterFactory(),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          fetchRecord: (args) => this.fetchRecord(args),
          submit: (patchObject) => this.patchRecord(patchObject),
          patchField: {
            formField: {
              key: "Status",
              name: "Status",
              type: "gridSelect",
              templateOptions: {
                label: "Status",
                options: [
                  {
                    label: "Active",
                    value: "Active",
                  },
                  {
                    label: "Inactive",
                    value: "Inactive",
                  },
                ],
                required: true,
                labelProp: "label",
                valueProp: "value",
              },
            },
          },
        }),
      },
      {
        field: "ProgressiveControllerTypeName",
        headerName: "Progressive Controller",
        filter: new FilterProvider("ProgressiveControllerTypeName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["ProgressiveControllerTypeId"],
              serverQuery: (search: string) =>
                serverUtility.lookupQuery({
                  url: progressiveControllerTypeUrl,
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          fetchRecord: (args) => this.fetchRecord(args),
          submit: (patchObject) => this.patchRecord(patchObject),
          patchField: {
            formField: fieldConfigurationHelperService.gridSelectAsyncField({
              getDefaultItem: (model) => {
                return {
                  Id: model.ProgressiveControllerTypeId,
                  Name: model.ProgressiveControllerTypeName,
                };
              },
              queryUrl: progressiveControllerTypeUrl,
              label: "Controller Type",
              key: "ProgressiveControllerTypeId",
              name: "ProgressiveControllerTypeId",
            }),
            referenceFieldName: "ProgressiveControllerTypeName",
          },
        }),
      },
      {
        field: "Version",
        headerName: "Version",
        filter: new FilterProvider("Version", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          fetchRecord: (args) => this.fetchRecord(args),
          submit: (patchObject) => this.patchRecord(patchObject),
          patchField: {
            formField: {
              ...fieldHelpers.simpleStringField({
                key: "Version",
                name: "Version",
                label: "Version",
              }),
              type: "gridInput",
            },
          },
        }),
      },
      {
        field: "Notes",
        headerName: "Notes",
        filter: new FilterProvider("Notes", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          fetchRecord: (args) => this.fetchRecord(args),
          submit: (patchObject) => this.patchRecord(patchObject),
          patchField: {
            formField: {
              ...fieldHelpers.textAreaField({
                key: "Notes",
                name: "Notes",
                label: "Notes",
                rows: 5,
              }),
              type: "gridTextarea",
            },
          },
        }),
      },
      {
        field: "TotalPercentage",
        requiresPermission: ProgressivePermissions.SensitiveInfoPermission,
        headerName: "Total Percentage",
        filter: new FilterProvider("TotalPercentage", $compile)
          .defaultNumberFilters()
          .getGridFilterFactory(),
        valueGetter: percentageValueGetterFactory({ decimalPlaces: 2 }),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          fetchRecord: (args) => this.fetchRecord(args),
          submit: (patchObject) => this.patchRecord(patchObject),
          patchField: {
            formField: {
              ...fieldHelpers.simplePercentageField({
                key: "TotalPercentage",
                name: "TotalPercentage",
                min: 0,
                max: 100,
                step: 0.01,
              }),
              type: "gridInput",
            },
          },
        }),
      },
    ];
  }

  private fetchRecord(args: CellDoubleClickedEventHandlerArgs) {
    return this.serverUtility.querySingle<Record>({
      url: progressiveDefinitionUrl,
      query: {
        Id: args.data.Id,
      },
    });
  }

  private patchRecord(patchObject: PatchObject) {
    return this.serverUtility.patch({
      url: progressiveDefinitionUrl,
      patchObject,
    });
  }
}
